import { useEffect, useState } from "react";
import { Search, Users, Hourglass } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState } from "@/components/common";
import { api, errMsg, fmtDate, fmtDateTime } from "@/lib/api";
import { MOVE_TYPES, fmtUnits } from "@/lib/loyalty";
import { cn } from "@/lib/utils";
import { StatusBadge } from "./LoyaltyActivities";
import { toast } from "sonner";

function CustomerMoves({ id, onClose, mode }) {
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); if (id) api.get(`/loyalty/customers/${id}`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, [id]);
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg" data-testid="loyalty-customer-sheet">
        {d && (<>
          <SheetHeader><SheetTitle>{d.customer.name}</SheetTitle></SheetHeader>
          <p className="text-sm text-muted-foreground">{d.customer.phone}</p>
          <div className="mt-4 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4">
            <p className="eyebrow">Saldo atual</p>
            <p className="font-display text-3xl font-bold" data-testid="loyalty-customer-balance">{fmtUnits(mode, d.customer.loyalty_balance)}</p>
            {d.next_expiry && <p className="mt-1 flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400" data-testid="loyalty-customer-expiry"><Hourglass className="h-3.5 w-3.5" />{fmtUnits(mode, d.next_expiry.units)} vencem em {fmtDate(d.next_expiry.at)}</p>}
          </div>
          <ul className="mt-4 divide-y text-sm" data-testid="loyalty-customer-moves">
            {d.moves.map((m) => (
              <li key={m.id} className="flex items-start justify-between gap-3 py-2.5">
                <span className="min-w-0"><span className="font-medium">{MOVE_TYPES[m.type] || m.type}</span> · {m.ref_label || m.note || "Manual"}
                  <span className="block text-xs text-muted-foreground">{fmtDateTime(m.created_at)}{m.user_name ? ` · ${m.user_name}` : ""}</span></span>
                <span className="flex shrink-0 flex-col items-end gap-1"><span className={cn("font-mono font-semibold", m.units < 0 ? "text-red-500" : "text-emerald-500")}>{m.units > 0 ? "+" : ""}{fmtUnits(mode, m.units)}</span><StatusBadge s={m.status} /></span>
              </li>
            ))}
            {!d.moves.length && <li className="py-8 text-center text-muted-foreground">Sem movimentos.</li>}
          </ul>
        </>)}
      </SheetContent>
    </Sheet>
  );
}

export function LoyaltyCustomers({ cfg }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => api.get("/loyalty/customers", { params: { q } }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))), 300);
    return () => clearTimeout(t);
  }, [q]);
  const u = (v) => fmtUnits(cfg.mode, v);
  return (
    <div className="space-y-4" data-testid="loyalty-customers">
      <div className="relative max-w-md"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pesquise por nome ou telefone" className="pl-9" data-testid="loyalty-customers-search" /></div>
      {rows && !rows.length ? <EmptyState icon={Users} title="Nenhum cliente com pontos ainda" text="Os clientes aparecem aqui assim que ganharem os primeiros pontos." /> : (
        <div className="card-surface overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Cliente</TableHead><TableHead className="text-right">Saldo</TableHead><TableHead className="text-right">Ganhos</TableHead><TableHead className="text-right">Resgatados</TableHead><TableHead className="text-right">Resgates</TableHead><TableHead className="text-right">Pendentes</TableHead><TableHead>Última atividade</TableHead></TableRow></TableHeader>
            <TableBody>{(rows || []).map((c) => (
              <TableRow key={c.id} onClick={() => setOpen(c.id)} className="cursor-pointer" data-testid={`loyalty-customer-row-${c.id}`}>
                <TableCell><span className="font-medium">{c.name}</span><span className="block text-xs text-muted-foreground">{c.phone}</span></TableCell>
                <TableCell className="text-right font-mono font-semibold text-amber-600 dark:text-amber-400">{u(c.balance)}</TableCell>
                <TableCell className="text-right font-mono">{u(c.earned)}</TableCell>
                <TableCell className="text-right font-mono">{u(c.redeemed)}</TableCell>
                <TableCell className="text-right font-mono">{c.redemptions}</TableCell>
                <TableCell className="text-right font-mono text-muted-foreground">{u(c.pending)}</TableCell>
                <TableCell className="whitespace-nowrap text-sm">{c.last ? fmtDateTime(c.last) : "—"}</TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        </div>
      )}
      <CustomerMoves id={open} onClose={() => setOpen(null)} mode={cfg.mode} />
    </div>
  );
}
